import { useMemo } from 'react'
import { CalendarClock, ShieldCheck, Sparkles, Plug } from 'lucide-react'
import { isAgentConnected } from '../lib/agentConnection'
import { useGetMonitoringStatusQuery } from '../services/sessionMonitoringApi'
import { useGetProjectQuery, useGetSecurityOwnershipQuery, useGetTestingSchedulesQuery } from '../services/projectDataApi'
import { testingScheduleSummary } from '../lib/testingScheduleSummary'
import ProjectSetupBanner from './ProjectSetupBanner'
import SupportCard, { type SupportStatus } from './SupportCard'

const loading: SupportStatus = { tone: 'yellow', text: 'Checking status…' }
const unavailable: SupportStatus = { tone: 'yellow', text: 'Status unavailable — open to retry' }
const setup: SupportStatus = { tone: 'red', text: 'Not configured', setup: true }
const disabled: SupportStatus = { tone: 'gray', text: 'Disabled' }

export default function TestingSetupActions({ projectId, bannerOnly = false }: { projectId: string; bannerOnly?: boolean }) {
  const monitoring = useGetMonitoringStatusQuery(projectId, { pollingInterval: 30000, skipPollingIfUnfocused: true })
  const schedules = useGetTestingSchedulesQuery(projectId, { pollingInterval: 60000, skipPollingIfUnfocused: true })
  const projectQuery = useGetProjectQuery(projectId, { pollingInterval: 15000, skipPollingIfUnfocused: true })
  const project = projectQuery.currentData
  const exempt = project?.security_ownership_exempt === true
  const ownership = useGetSecurityOwnershipQuery({ projectId, targetUrl: project?.target_url ?? '', nonce: project?.security_nonce ?? '' }, {
    skip: !project?.security_nonce || exempt || !project.enabled_polish_passes.includes('security') || !project.security_probing_enabled,
    pollingInterval: 15000, skipPollingIfUnfocused: true,
  })
  const status = monitoring.currentData
  const savedSchedules = schedules.currentData?.schedules
  const currentMinute = Math.floor(Date.now() / 60000)
  const scheduleSummary = useMemo(() => bannerOnly || !savedSchedules ? ''
    : testingScheduleSummary(savedSchedules, new Date(currentMinute * 60000)), [savedSchedules, currentMinute, bannerOnly])
  if (bannerOnly) {
    const setupComplete = Boolean(project && status?.has_sessions && schedules.currentData?.schedules.some(schedule => schedule.config.enabled)
      && (!project.enabled_polish_passes.includes('security') || exempt || ownership.currentData?.latest?.verified))
    return status?.can_edit ? <ProjectSetupBanner key={projectId} projectId={projectId} complete={setupComplete} /> : null
  }
  const healing: SupportStatus = monitoring.isError ? unavailable : !status ? loading
    : !status.configured || (!status.has_sessions && !status.enabled) ? { tone: 'red', text: 'Watch user sessions for bugs', setup: true } : !status.enabled ? disabled
    : !status.has_sessions ? { tone: 'yellow', text: 'Waiting for the first session' }
    : { tone: 'white', text: 'Reviewing user sessions' }
  const testing: SupportStatus = schedules.isError ? unavailable : !savedSchedules ? loading
    : savedSchedules.length === 0 ? setup : !savedSchedules.some(schedule => schedule.config.enabled) ? disabled
    : { tone: 'white', text: scheduleSummary }
  const securitySetup: SupportStatus = { tone: 'red', text: 'Site ownership not verified', setup: true }
  const security: SupportStatus = projectQuery.isError ? unavailable : !project ? loading
    : !exempt && !project.security_nonce ? securitySetup
    : !project.enabled_polish_passes.includes('security') || !project.security_probing_enabled ? disabled
    : exempt ? { tone: 'white', text: 'Security testing enabled' }
    : ownership.isError ? unavailable : !ownership.currentData ? loading
    : !ownership.currentData.latest?.verified ? securitySetup
    : { tone: 'white', text: 'Ownership verified · security testing enabled' }
  const agent: SupportStatus = projectQuery.isError ? unavailable : !project ? loading
    : isAgentConnected(project) ? { tone: 'white', text: 'Agent connected' } : { tone: 'red', text: 'Agent not connected' }
  return <div className="testing-setup-actions" aria-label="Project support">
    <SupportCard title="Self Healing" icon={Sparkles} to={`/projects/${projectId}/support/self-healing`} status={healing} />
    <SupportCard title="Test Schedule" icon={CalendarClock} to={`/projects/${projectId}/support/test-schedule`} status={testing} />
    <SupportCard title="Security" icon={ShieldCheck} to={`/projects/${projectId}/security`} status={security} />
    <SupportCard title="Agent Connection" icon={Plug} to={`/projects/${projectId}/support/agent-connection`} status={agent} />
  </div>
}
